// storageSheetService.js — bulk storage-method editing via a spreadsheet
// (Brian, 2026-08-20). Download a CSV of all products (opens directly in
// Excel), type r / f / d in the Storage column, upload it back. Blank = leave
// that product unchanged, so the sheet can be filled in over several passes.
//
// The upload reads ONLY the SKU and Storage columns — product names in the
// file are for the admin's eyes and are never written back, so Excel
// re-encoding Korean text on save can't corrupt anything.
import Papa from "papaparse";
import { db } from "../config/firebase";
import { doc, writeBatch } from "firebase/firestore";
import { invalidateProductCache } from "./dataCache";

const BATCH_SIZE = 400; // Firestore caps batches at 500 writes

// Everything we accept in the Storage column, normalized to the canonical
// English strings stored on product docs (see AdminProducts STORAGE_METHODS
// and utils/orderSort.js).
const VALUE_MAP = {
  r: "Refrigerated", refrigerated: "Refrigerated", "냉장": "Refrigerated",
  f: "Frozen", frozen: "Frozen", "냉동": "Frozen",
  d: "Dry", dry: "Dry", "건조": "Dry", "실온": "Dry"
};

const LETTER_FOR = { Refrigerated: "r", Frozen: "f", Dry: "d" };

// Column layout of the downloaded sheet — the upload expects the same order.
const HEADER = ["SKU", "Product Name", "Storage (r/f/d)"];

function skuNum(sku) {
  const n = parseInt(sku, 10);
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}

/**
 * Build the storage sheet and hand it to the browser as a download.
 * Products with NO storage method sort to the top (the admin's to-do list),
 * each group ordered by SKU. UTF-8 BOM so Excel renders Korean names.
 */
export function downloadStorageSheet(products) {
  // Discontinued items don't need storage methods — keep them off the to-do
  // sheet entirely (Brian, 2026-08-20).
  const rows = products.filter(p => !p.discontinued && !p.keepHidden)
    .sort((a, b) => {
      const aSet = a.storageMethod ? 1 : 0;
      const bSet = b.storageMethod ? 1 : 0;
      if (aSet !== bSet) return aSet - bSet; // unset first
      return skuNum(a.sku) - skuNum(b.sku);
    })
    .map(p => [p.sku, p.name || "", LETTER_FOR[p.storageMethod] || ""]);

  const csv = Papa.unparse({ fields: HEADER, data: rows });
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sunjin-storage-methods-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return rows.length;
}

/**
 * Parse an uploaded (filled-in) sheet and apply the storage methods.
 * Only rows whose value actually CHANGES are written. Returns a report:
 * { updated, skippedBlank, unchanged, unknownValues: [{sku, value}],
 *   unknownSkus: [sku] } — nothing fails silently.
 */
export async function applyStorageSheet(file, products) {
  const text = await file.text();
  const parsed = Papa.parse(text.replace(/^﻿/, ""), { skipEmptyLines: true });
  if (parsed.errors.length > 0 && parsed.data.length === 0) {
    throw new Error("Could not read that file as a spreadsheet. Upload the CSV downloaded from this page.");
  }

  let rows = parsed.data;
  // Drop the header row if present (first cell says "SKU").
  if (rows.length > 0 && String(rows[0][0] || "").trim().toLowerCase() === "sku") {
    rows = rows.slice(1);
  }
  if (rows.length === 0) {
    throw new Error("The file has no data rows. Nothing was changed.");
  }

  const bySku = new Map(products.map(p => [String(p.sku), p]));
  const changes = []; // { sku, method }
  const report = { updated: 0, skippedBlank: 0, unchanged: 0, unknownValues: [], unknownSkus: [] };

  for (const row of rows) {
    const sku = String(row[0] ?? "").trim();
    if (!sku) continue; // fully blank line
    const product = bySku.get(sku);
    if (!product) {
      report.unknownSkus.push(sku);
      continue;
    }
    const raw = String(row[2] ?? "").trim();
    if (!raw) {
      report.skippedBlank++;
      continue;
    }
    const method = VALUE_MAP[raw.toLowerCase()];
    if (!method) {
      report.unknownValues.push({ sku, value: raw });
      continue;
    }
    if ((product.storageMethod || "") === method) {
      report.unchanged++;
      continue;
    }
    changes.push({ sku, method });
  }

  for (let i = 0; i < changes.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    for (const c of changes.slice(i, i + BATCH_SIZE)) {
      batch.update(doc(db, "products", c.sku), { storageMethod: c.method });
    }
    await batch.commit();
    report.updated += Math.min(BATCH_SIZE, changes.length - i);
  }

  if (report.updated > 0) invalidateProductCache();
  return { report, changes };
}
